import { Locale } from "@dealer-center/ddc-core-utils";
import { Tabs } from "@dealer-center/core-components";
import { IncentivesTable } from "./IncentivesTable";
import type {
  IncentivesPayload,
  IncentivesRecord
} from "@dealer-center/ddc-pages-incentives-manager-interface";
import { useContext, useEffect, useState } from "react";
import { TableAttributesContext } from "../providers/TableAttributesProvider";
import styled from "styled-components";
import { TabActionPanel } from "./TabActionPanel";
import { getTabStates } from "../utils/utils";

interface TabData {
  label: string;
  value: string | number;
  active?: boolean;
}

interface TabProps {
  hasAnyUpstreamCompliance: boolean;
  incentives: null | IncentivesPayload;
  showIncentivePopup?: (
    isShowing: boolean,
    type: string,
    incentive?: IncentivesRecord
  ) => void;
  tabIndex: number;
  setTabIndex: (index: number) => void;
}

const TABS_ID = "tabs";

const IncentivesManagerTabBar = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const setupTableAttributes = (
  index: number,
  selectedAttributes,
  setTableAttributes
) => {
  const ta = selectedAttributes.filter(attribute => {
    return (index === 0 && attribute === "SOURCE") ||
      (index !== 1 && attribute === "ACTION_NEEDED")
      ? false
      : true;
  });
  setTableAttributes(ta);
};

export const TabContainer = ({
  hasAnyUpstreamCompliance,
  incentives,
  showIncentivePopup,
  tabIndex,
  setTabIndex
}: TabProps) => {
  const [tabs, setTabs] = useState<TabData[]>([]);
  const { selectedAttributes } = useContext(TableAttributesContext);
  const [tableAttributes, setTableAttributes] = useState<string[]>([]);
  const [tableSelection, setTableSelection] = useState<number[]>([]);

  useEffect(() => {
    const buildTabs = (incentives, tabIdx = tabIndex) => {
      if (incentives && incentives.logicalStates) {
        const tabData: TabData[] = getTabStates(
          incentives,
          hasAnyUpstreamCompliance
        ).map((state, index) => {
          return {
            active: index === tabIdx,
            label: `${Locale.getText(state.logicalState)} (${state.totalIncentives})`,
            value: index
          };
        });
        setupTableAttributes(tabIndex, selectedAttributes, setTableAttributes);
        return tabData;
      }
      return [];
    };

    if (incentives) {
      let tabidx = tabIndex;
      if (tabIndex === -1) {
        incentives.logicalStates.find((state, index) => {
          if (state.logicalState === "PUBLISHED") {
            setTabIndex(index);
            tabidx = index;
            return true;
          }
          return false;
        });
      }
      setTabs(buildTabs(incentives, tabidx));
    }
  }, [incentives, hasAnyUpstreamCompliance, selectedAttributes, tabIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Interstate's Tabs permanently misreports its scroll-chevron overflow check, so recompute it ourselves and toggle the buttons directly via their stable analytics attribute.
  useEffect(() => {
    const root = document.getElementById(TABS_ID);
    const scroller = root?.querySelector<HTMLElement>(".MuiTabs-scroller");
    if (!root || !scroller) return undefined;

    const updateScrollButtonVisibility = () => {
      // Either button may be absent; match each by its DOM position relative to the scroller, not CSS offsets (which real browsers can resolve to a used pixel value on both sides).
      const arrowButtons = Array.from(
        root.querySelectorAll<HTMLElement>(
          ":scope > [data-ids-react-component-name='ids-react-noTag']"
        )
      );
      const startButton = arrowButtons.find(
        button =>
          scroller.compareDocumentPosition(button) &
          Node.DOCUMENT_POSITION_PRECEDING
      );
      const endButton = arrowButtons.find(
        button =>
          scroller.compareDocumentPosition(button) &
          Node.DOCUMENT_POSITION_FOLLOWING
      );
      const canScrollLeft = scroller.scrollLeft > 1;
      const canScrollRight =
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;
      if (startButton) {
        startButton.style.display = canScrollLeft ? "" : "none";
      }
      if (endButton) {
        endButton.style.display = canScrollRight ? "" : "none";
      }
    };

    updateScrollButtonVisibility();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateScrollButtonVisibility)
        : undefined;
    resizeObserver?.observe(root);

    const mutationObserver =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(updateScrollButtonVisibility)
        : undefined;
    mutationObserver?.observe(root, {
      childList: true,
      subtree: true
    });

    scroller.addEventListener("scroll", updateScrollButtonVisibility);

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      scroller.removeEventListener("scroll", updateScrollButtonVisibility);
    };
  }, [tabs]);

  const onTabActivate = (index: number) => {
    setTabIndex(index);
    setupTableAttributes(index, selectedAttributes, setTableAttributes);
  };

  const handleRowDoubleClick = (record: IncentivesRecord) => {
    if (!showIncentivePopup) return;

    const currentTab = incentives?.logicalStates[tabIndex]?.logicalState || "";

    // DECLINED tab doesn't have Edit or Open capability, so don't open anything
    if (currentTab === "DECLINED") {
      return;
    }

    const type = currentTab === "EXPIRED" ? "OPEN_INCENTIVE" : "EDIT_INCENTIVE";
    showIncentivePopup(true, type, record);
  };

  if (tabs.length === 0) {
    return null;
  }
  return (
    <IncentivesManagerTabBar>
      <Tabs id={TABS_ID} tabs={tabs} onActivate={onTabActivate} />
      <TabActionPanel
        currentTab={incentives?.logicalStates[tabIndex]?.logicalState || ""}
        incentives={
          incentives?.logicalStates[tabIndex]?.incentives as IncentivesRecord[]
        }
        selections={tableSelection}
        showIncentivePopup={showIncentivePopup}
      />
      <IncentivesTable
        columns={tableAttributes}
        data={
          incentives?.logicalStates[tabIndex]?.incentives as IncentivesRecord[]
        }
        onRowDoubleClick={handleRowDoubleClick}
        onSelections={selectedIndices => {
          setTableSelection(selectedIndices);
        }}
      />
    </IncentivesManagerTabBar>
  );
};
